import React, { useEffect, useState } from 'react';
import {
    biggestSpenders,
    flagVolumeByRule,
    flagVolumeByManager,
    flagVolumeTimeseries,
} from './anomalyApi';

const s = {
    page:   { padding: 24, background: '#f8fafc', minHeight: '100vh', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' },
    h1:     { fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 },
    sub:    { fontSize: 13, color: '#64748b', marginTop: 4 },
    grid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16, marginTop: 16 },
    card:   { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 },
    cardH:  { fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 },
    table:  { width: '100%', fontSize: 12, borderCollapse: 'collapse' },
    th:     { textAlign: 'left', padding: '6px 8px', color: '#475569', fontWeight: 600, borderBottom: '1px solid #e2e8f0' },
    td:     { padding: '6px 8px', borderBottom: '1px solid #f1f5f9', color: '#0f172a' },
    tdR:    { padding: '6px 8px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    empty:  { fontSize: 12, color: '#64748b', padding: 12 },
    err:    { padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13 },
};

export default function AnomalyReports() {
    const [pcardTop, setPcardTop] = useState([]);
    const [fuelTop, setFuelTop]   = useState([]);
    const [byRule, setByRule]     = useState([]);
    const [byMgr, setByMgr]       = useState([]);
    const [timeseries, setTimeseries] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [a, b, c, d, e] = await Promise.all([
                    biggestSpenders({ source: 'pcard', limit: 15 }),
                    biggestSpenders({ source: 'fuelcard', limit: 15 }),
                    flagVolumeByRule(),
                    flagVolumeByManager(),
                    flagVolumeTimeseries(30),
                ]);
                setPcardTop(a.data || []);
                setFuelTop(b.data || []);
                setByRule(c.data || []);
                setByMgr(d.data || []);
                setTimeseries(e.data || []);
            } catch (err) {
                setError(err.message);
            }
        })();
    }, []);

    return (
        <div style={s.page}>
            <h1 style={s.h1}>Anomaly reports</h1>
            <div style={s.sub}>Roll-ups over the last 30 days. Pure SQL aggregations — no AI.</div>

            {error && <div style={{ ...s.err, marginTop: 12 }}>Error: {error}</div>}

            <div style={s.grid}>
                <SpenderCard title="Biggest P-card spenders (30d)" rows={pcardTop} />
                <SpenderCard title="Biggest fuel-card spenders (30d)" rows={fuelTop} />
                <RuleCard rows={byRule} />
                <ManagerCard rows={byMgr} />
                <TimeseriesCard rows={timeseries} />
            </div>
        </div>
    );
}

function SpenderCard({ title, rows }) {
    return (
        <div style={s.card}>
            <div style={s.cardH}>{title}</div>
            {rows.length === 0 && <div style={s.empty}>No data yet — enrich + run the engine first.</div>}
            {rows.length > 0 && (
                <table style={s.table}>
                    <thead>
                        <tr>
                            <th style={s.th}>Employee</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>Txns</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>Total</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>Avg</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.employee_id}>
                                <td style={s.td}>{r.employee_name || r.employee_id}</td>
                                <td style={s.tdR}>{r.txn_count}</td>
                                <td style={s.tdR}>${Number(r.total_spend_usd || 0).toFixed(0)}</td>
                                <td style={s.tdR}>${Number(r.avg_txn_usd || 0).toFixed(0)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function RuleCard({ rows }) {
    return (
        <div style={s.card}>
            <div style={s.cardH}>Flag volume by rule (30d)</div>
            {rows.length === 0 && <div style={s.empty}>No flags yet.</div>}
            {rows.length > 0 && (
                <table style={s.table}>
                    <thead>
                        <tr>
                            <th style={s.th}>Rule</th>
                            <th style={s.th}>Sev</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>Total</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>Open</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>Resolved</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={`${r.rule_id}-${r.severity}`}>
                                <td style={s.td}><code style={{ fontSize: 11 }}>{r.rule_id}</code></td>
                                <td style={s.td}>{r.severity}</td>
                                <td style={s.tdR}>{r.total}</td>
                                <td style={s.tdR}>{r.open_count}</td>
                                <td style={s.tdR}>{r.resolved_count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function ManagerCard({ rows }) {
    return (
        <div style={s.card}>
            <div style={s.cardH}>Flags by manager (30d)</div>
            {rows.length === 0 && <div style={s.empty}>No flags yet.</div>}
            {rows.length > 0 && (
                <table style={s.table}>
                    <thead>
                        <tr>
                            <th style={s.th}>Manager</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>Total</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>Open</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>High</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.manager_employee_id || 'unassigned'}>
                                <td style={s.td}>{r.manager_employee_id || '(unassigned)'}</td>
                                <td style={s.tdR}>{r.total}</td>
                                <td style={s.tdR}>{r.open_count}</td>
                                <td style={s.tdR}>{r.high_count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function TimeseriesCard({ rows }) {
    const byDay = {};
    for (const r of rows) {
        const k = String(r.day).slice(0, 10);
        byDay[k] ||= { high: 0, medium: 0, low: 0 };
        byDay[k][r.severity] = r.n;
    }
    const days = Object.keys(byDay).sort();
    return (
        <div style={s.card}>
            <div style={s.cardH}>Daily flag volume by severity</div>
            {days.length === 0 && <div style={s.empty}>No flags yet.</div>}
            {days.length > 0 && (
                <table style={s.table}>
                    <thead>
                        <tr>
                            <th style={s.th}>Day</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>High</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>Medium</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>Low</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {days.map(d => {
                            const r = byDay[d];
                            const total = (r.high || 0) + (r.medium || 0) + (r.low || 0);
                            return (
                                <tr key={d}>
                                    <td style={s.td}>{d}</td>
                                    <td style={s.tdR}>{r.high || 0}</td>
                                    <td style={s.tdR}>{r.medium || 0}</td>
                                    <td style={s.tdR}>{r.low || 0}</td>
                                    <td style={s.tdR}>{total}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}
